import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { BASE_URL } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, X, ChevronDown, ChevronUp } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  color: string;
  prices?: Record<string, number>;
  prices2?: Record<string, number>;
  customPrices?: Record<string, number | undefined>;
  customPricesTeacher?: Record<string, number | undefined>;
  useCustomPrices?: boolean;
  s_std_pay_type?: string | number;
  s_thr_pay_type?: string | number;
  s_tch_pay_type?: string | number;
}

interface Teacher {
  id: string;
  firstname: string;
  lastname: string;
  email?: string;
}

interface TeacherCategoryPricesModalProps {
  teacher: Teacher;
  onClose: () => void;
}

const TeacherCategoryPricesModal: React.FC<TeacherCategoryPricesModalProps> = ({ teacher, onClose }) => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const { toast } = useToast();

  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current && categories.length > 0) {
      setOpenCategoryId(categories[0].id);
      isFirstLoad.current = false;
    }
  }, [categories]);

  useEffect(() => {
    fetchCategoriesAndCustomPrices();
  }, [token, teacher.id]);

  const fetchCategoriesAndCustomPrices = async () => {
  setIsLoading(true);
  try {
    const categoriesResponse = await fetch(`${BASE_URL}/api/categories`, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!categoriesResponse.ok) {
      throw new Error('Failed to fetch categories');
    }

    const categoriesData = await categoriesResponse.json();

    let categoriesList = categoriesData.categories || [];

    categoriesList = categoriesList.map(category => ({
      ...category,
      customPrices: { ...category.prices },
      customPricesTeacher: { ...category.prices2 },
      useCustomPrices: false
    }));

    const categoryIds = categoriesList.map(category => category.id);

    const customPricesResponse = await fetch(`${BASE_URL}/api/teachers/${teacher.id}/get-category-prices`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ category_ids: categoryIds })
    });

    if (customPricesResponse.ok) {
      const customPricesData = await customPricesResponse.json();

      categoriesList = categoriesList.map(category => {
        const customPriceData = customPricesData.prices?.find(
          (cp: any) => cp.category_id === category.id
        );

        if (customPriceData) {
          return {
            ...category,
            customPrices: customPriceData.prices,
            customPricesTeacher: customPriceData.pricesTeacher,
            useCustomPrices: true
          };
        }
        return category;
      });
    }

    setCategories(categoriesList);
  } catch (error) {
    console.error('Error fetching data:', error);
    toast({
      title: 'Error',
      description: 'Failed to load categories and pricing data. Using default prices.',
      variant: 'destructive',
    });
  } finally {
    setIsLoading(false);
  }
};

  const handleToggleCustomPrices = (categoryId: string, value: boolean) => {
    setCategories(prevCategories =>
      prevCategories.map(category => {
        if (category.id !== categoryId) return category;

        return {
          ...category,
          useCustomPrices: value,
          customPrices: value ? { ...category.prices } : { ...category.prices },
          customPricesTeacher: value ? { ...category.prices2 } : { ...category.prices2 }
        };
      })
    );
  };

  const handlePriceChange = (categoryId: string, priceKey: string, value: string, isStudent: boolean) => {
    const numericValue = value === '' ? undefined : parseFloat(value);

    setCategories(prevCategories =>
      prevCategories.map(category => {
        if (category.id !== categoryId) return category;

        if (isStudent) {
          const newCustomPrices = {
            ...(category.customPrices || {}),
            [priceKey]: numericValue
          };

          return {
            ...category,
            customPrices: newCustomPrices
          };
        } else {
          const newCustomPricesTeacher = {
            ...(category.customPricesTeacher || {}),
            [priceKey]: numericValue
          };

          return {
            ...category,
            customPricesTeacher: newCustomPricesTeacher
          };
        }
      })
    );
  };

  const saveCustomPrices = async () => {
    setIsSaving(true);

    try {
      const customPricesData = categories
        .filter(category => category.useCustomPrices)
        .map(category => ({
          category_id: category.id,
          pricesStudent: category.customPrices,
          pricesTeacher: category.customPricesTeacher
        }));

      const response = await fetch(`${BASE_URL}/api/teachers/${teacher.id}/category-prices`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ prices: customPricesData })
      });

      if (!response.ok) {
        throw new Error('Failed to save custom prices');
      }

      toast({
        title: 'Success',
        description: 'Custom prices saved successfully',
      });

      onClose();
    } catch (error) {
      console.error('Error saving custom prices:', error);
      toast({
        title: 'Error',
        description: 'Failed to save custom prices',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setOpenCategoryId(openCategoryId === categoryId ? null : categoryId);
  };

  const renderPriceInputs = (category: Category) => {
    // 0 = hourly rate, 1 = fixed price
    const studentPayType = category.s_std_pay_type != null
      ? parseInt(String(category.s_std_pay_type), 10)
      : 0;

    const teacherPayTypeValue = category.s_thr_pay_type ?? category.s_tch_pay_type;
    const teacherPayType = teacherPayTypeValue != null
      ? parseInt(String(teacherPayTypeValue), 10)
      : studentPayType;

    return (
      <Tabs defaultValue="teacher">
        <TabsList>
          <TabsTrigger value="teacher">{t('categories.tabTeachers')}</TabsTrigger>
          <TabsTrigger value="student">{t('categories.tabStudents')}</TabsTrigger>
        </TabsList>
        <TabsContent value="student">
          <div className="space-y-2 mt-4">
            {studentPayType === 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => {
                  const priceKey = `price_${num}`;
                  return (
                    <div key={priceKey}>
                      <Label htmlFor={`${category.id}-${priceKey}`}>
                        {num} {t('students.student')}{num > 1 ? 's' : ''}
                      </Label>
                      <Input
                        id={`${category.id}-${priceKey}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={category.customPrices?.[priceKey] ?? ''}
                        onChange={(e) => handlePriceChange(category.id, priceKey, e.target.value, true)}
                        disabled={!category.useCustomPrices}
                        className="mt-1"
                      />
                    </div>
                  );
                })}
                <div>
                  <Label htmlFor={`${category.id}-price_su`}>
                    {t('teachers.moreThan20')}
                  </Label>
                  <Input
                    id={`${category.id}-price_su`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={category.customPrices?.['price_su'] ?? ''}
                    onChange={(e) => handlePriceChange(category.id, 'price_su', e.target.value, true)}
                    disabled={!category.useCustomPrices}
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor={`${category.id}-price_fix`}>{t('teachers.priceFix')}</Label>
                <Input
                  id={`${category.id}-price_fix`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={category.customPrices?.['price_fix'] ?? ''}
                  onChange={(e) => handlePriceChange(category.id, 'price_fix', e.target.value, true)}
                  disabled={!category.useCustomPrices}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </TabsContent>
        <TabsContent value="teacher">
          <div className="space-y-2 mt-4">
            {teacherPayType === 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => {
                  const priceKey = `price_${num}`;
                  return (
                    <div key={priceKey}>
                      <Label htmlFor={`${category.id}-teacher-${priceKey}`}>
                        {num} {t('students.student')}{num > 1 ? 's' : ''}
                      </Label>
                      <Input
                        id={`${category.id}-teacher-${priceKey}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={category.customPricesTeacher?.[priceKey] ?? ''}
                        onChange={(e) => handlePriceChange(category.id, priceKey, e.target.value, false)}
                        disabled={!category.useCustomPrices}
                        className="mt-1"
                      />
                    </div>
                  );
                })}
                <div>
                  <Label htmlFor={`${category.id}-teacher-price_su`}>
                    {t('teachers.moreThan20')}
                  </Label>
                  <Input
                    id={`${category.id}-teacher-price_su`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={category.customPricesTeacher?.['price_su'] ?? ''}
                    onChange={(e) => handlePriceChange(category.id, 'price_su', e.target.value, false)}
                    disabled={!category.useCustomPrices}
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor={`${category.id}-teacher-price_fix`}>{t('teachers.priceFix')}</Label>
                <Input
                  id={`${category.id}-teacher-price_fix`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={category.customPricesTeacher?.['price_fix'] ?? ''}
                  onChange={(e) => handlePriceChange(category.id, 'price_fix', e.target.value, false)}
                  disabled={!category.useCustomPrices}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-medium">
          {t('teachers.customPricesFor')} {teacher.firstname} {teacher.lastname}
        </h3>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="space-y-4 mt-4">
            {categories.map(category => (
              <div key={category.id} className="p-4 border rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }}></div>
                    <span className="font-medium">{category.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`toggle-${category.id}`} className="text-sm">
                        {t('teachers.useCustomPrices')}
                      </Label>
                      <Switch
                        id={`toggle-${category.id}`}
                        checked={category.useCustomPrices || false}
                        onCheckedChange={(value) => handleToggleCustomPrices(category.id, value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCategory(category.id)}
                    >
                      {openCategoryId === category.id ? <ChevronUp /> : <ChevronDown />}
                    </Button>
                  </div>
                </div>
                {openCategoryId === category.id && renderPriceInputs(category)}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              <X className="mr-2 h-4 w-4" />
              {t('common.cancel')}
            </Button>
            <Button onClick={saveCustomPrices} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {t('common.save')}
              {isSaving && (
                <div className="ml-2 animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-background"></div>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default TeacherCategoryPricesModal;
